/**
 * Sparkline — a tiny inline SVG line chart for a metric's recent trend.
 *
 * Hand-rolled SVG (the codebase ships no chart library — bundle size
 * matters for Lambda). Themed via `currentColor` so callers colour it
 * by wrapping in a `text-kw-*` class. Renders responsively through a
 * fixed `viewBox` + `width: 100%`.
 *
 * Input is oldest→newest by default. Overview series arrive DESCENDING
 * (index 0 = most recent), so pass `reversed` to flip them for display.
 *
 * Gaps (null points) break the line into segments rather than drawing a
 * misleading straight bridge across missing weeks; each real point is
 * still dotted so a lone data point remains visible. All-null and
 * single-point inputs degrade gracefully.
 */

export interface SparklineProps {
  /** Values in the plotted order; nulls are gaps (no entry that week). */
  values: (number | null)[];
  /** Reverse before plotting — use when the source is DESCENDING. */
  reversed?: boolean;
  width?: number;
  height?: number;
  className?: string;
  'aria-label'?: string;
}

const VIEW_W = 100;
const PAD_Y = 2;

export function Sparkline({
  values,
  reversed = false,
  width = 100,
  height = 24,
  className,
  'aria-label': ariaLabel,
}: SparklineProps) {
  const ordered = reversed ? [...values].reverse() : values;
  const n = ordered.length;

  const present = ordered.filter((v): v is number => v !== null);
  const hasData = present.length > 0;

  const min = hasData ? Math.min(...present) : 0;
  const max = hasData ? Math.max(...present) : 0;
  const span = max - min;

  // x maps index → [0, VIEW_W]; a single column pins to the centre.
  const x = (i: number): number => (n <= 1 ? VIEW_W / 2 : (i / (n - 1)) * VIEW_W);
  // y inverts (SVG origin is top-left); a flat series sits on the mid-line.
  const y = (v: number): number => {
    const plotH = height - PAD_Y * 2;
    if (span === 0) return PAD_Y + plotH / 2;
    return PAD_Y + plotH - ((v - min) / span) * plotH;
  };

  // Split into contiguous non-null runs so gaps break the stroke.
  const segments: { x: number; y: number }[][] = [];
  let run: { x: number; y: number }[] = [];
  ordered.forEach((v, i) => {
    if (v === null) {
      if (run.length) segments.push(run);
      run = [];
    } else {
      run.push({ x: x(i), y: y(v) });
    }
  });
  if (run.length) segments.push(run);

  return (
    <svg
      viewBox={`0 0 ${String(VIEW_W)} ${String(height)}`}
      width={width}
      height={height}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel ?? 'Trend sparkline'}
      className={className}
      style={{ width: '100%', maxWidth: width, height }}
    >
      {!hasData ? (
        <line
          x1={0}
          y1={height / 2}
          x2={VIEW_W}
          y2={height / 2}
          stroke="currentColor"
          strokeOpacity={0.25}
          strokeDasharray="2 2"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      ) : (
        <>
          {segments.map((seg, si) => (
            <polyline
              key={si}
              points={seg.map((p) => `${String(p.x)},${String(p.y)}`).join(' ')}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {/* A lone point between gaps draws no stroke (a 1-point polyline is
              invisible), so dot every isolated point to keep it on the chart. */}
          {segments.map((seg, si) => {
            const solo = seg.length === 1 ? seg[0] : null;
            return solo ? (
              <circle
                key={`dot-${String(si)}`}
                cx={solo.x}
                cy={solo.y}
                r={1.5}
                fill="currentColor"
                vectorEffect="non-scaling-stroke"
              />
            ) : null;
          })}
          {/* Emphasise the most recent point so the eye lands on "now". */}
          {(() => {
            const lastSeg = segments.at(-1);
            const lastPt = lastSeg?.at(-1);
            if (!lastPt) return null;
            return (
              <circle
                cx={lastPt.x}
                cy={lastPt.y}
                r={1.75}
                fill="currentColor"
                vectorEffect="non-scaling-stroke"
              />
            );
          })()}
        </>
      )}
    </svg>
  );
}
